import { useEffect, useState } from "react";
import {
  adminGetStudents, adminCreateStudent, adminUpdateStudent, adminDeleteStudent,
  adminGetSections, adminCreateSection, adminDeleteSection,
} from "../services/api";
import ConfirmModal from "./ConfirmModal";

const EMPTY = { first_name:"", last_name:"", email:"", password:"", code:"", section_id:"" };

export default function StudentManager() {
  const [students,  setStudents]  = useState([]);
  const [sections,  setSections]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [form,      setForm]      = useState(EMPTY);
  const [editId,    setEditId]    = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");
  const [success,   setSuccess]   = useState("");
  const [newSec,    setNewSec]    = useState("");
  const [savingSec, setSavingSec] = useState(false);
  const [secError,  setSecError]  = useState("");
  const [search,    setSearch]    = useState("");
  const [filterSec, setFilterSec] = useState("");
  const [sortKey,   setSortKey]   = useState("first_name");
  const [sortAsc,   setSortAsc]   = useState(true);

  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, sec] = await Promise.all([adminGetStudents(), adminGetSections()]);
      setStudents(s);
      setSections(sec);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc((a) => !a);
    else { setSortKey(key); setSortAsc(true); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    if (!form.first_name || !form.last_name || !form.email)
      return setError("Nombre, apellido y email son requeridos.");
    if (!editId && !form.password) return setError("La contraseña es requerida al crear.");
    if (form.password && form.password.length < 6) return setError("Contraseña mínimo 6 caracteres.");
    setSaving(true);
    try {
      const body = { ...form, section_id: form.section_id || null };
      if (editId) {
        await adminUpdateStudent(editId, body);
        setSuccess("Alumno actualizado correctamente.");
      } else {
        await adminCreateStudent(body);
        setSuccess(`Alumno "${form.first_name} ${form.last_name}" creado correctamente.`);
      }
      setForm(EMPTY); setEditId(null);
      load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleEdit = (s) => {
    setForm({
      first_name: s.first_name, last_name: s.last_name,
      email: s.email, password: "", code: s.code||"", section_id: s.section_id||""
    });
    setEditId(s.id);
    setError(""); setSuccess("");
    window.scrollTo({ top: 0, behavior:"smooth" });
  };

  const handleDelete = (id, name) => {
    setConfirm({
      title: "¿Eliminar alumno?",
      message: `"${name}" será eliminado permanentemente. Esta acción no se puede deshacer.`,
      onConfirm: async () => {
        try {
          await adminDeleteStudent(id);
          setStudents((p) => p.filter((s) => s.id !== id));
        } catch (err) { setError(err.message); }
        finally { setConfirm(null); }
      },
    });
  };

  const handleCreateSection = async (e) => {
    e.preventDefault();
    if (!newSec.trim()) return;
    setSecError(""); setSavingSec(true);
    try {
      await adminCreateSection({ name: newSec.trim() });
      setNewSec("");
      setSections(await adminGetSections());
    } catch (err) { setSecError(err.message); }
    finally { setSavingSec(false); }
  };

  const handleDeleteSection = (id, name) => {
    setConfirm({
      title: "¿Eliminar sección?",
      message: `La sección "${name}" será eliminada. Los alumnos asignados quedarán sin sección.`,
      onConfirm: async () => {
        try {
          await adminDeleteSection(id);
          load();
        } catch (err) { setError(err.message); }
        finally { setConfirm(null); }
      },
    });
  };

  const filtered = students
    .filter((s) => {
      const q        = search.toLowerCase();
      const fullName = `${s.first_name} ${s.last_name}`.toLowerCase();
      const hit      = !search || fullName.includes(q) || (s.code||"").includes(q) || (s.email||"").toLowerCase().includes(q);
      const sec      = !filterSec || String(s.section_id) === String(filterSec);
      return hit && sec;
    })
    .sort((a, b) => {
      const va = (a[sortKey] || "").toString().toLowerCase();
      const vb = (b[sortKey] || "").toString().toLowerCase();
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ?  1 : -1;
      return 0;
    });

  const countLabel = (search || filterSec) && filtered.length !== students.length
    ? `${filtered.length} de ${students.length}`
    : students.length;

  const SortTh = ({ label, field, width }) => {
    const active = sortKey === field;
    return (
      <th
        onClick={() => handleSort(field)}
        style={{
          fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "0.7rem",
          color: active ? "var(--teal)" : "var(--teal-dark)",
          textAlign: "left", padding: "8px 10px", textTransform: "uppercase",
          letterSpacing: .5, whiteSpace: "nowrap", width,
          cursor: "pointer", userSelect: "none",
          background: active ? "var(--teal-light)" : "transparent",
          transition: "color 0.15s, background 0.15s",
        }}
      >
        {label} {active ? (sortAsc ? "↑" : "↓") : <span style={{ opacity: 0.35 }}>↕</span>}
      </th>
    );
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1.5rem" }}>

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {/* ── Secciones ── */}
      <div className="card">
        <h2 style={{ fontFamily:"var(--font-display)", fontWeight:800, fontSize:"0.95rem", color:"var(--teal-dark)", marginBottom:".8rem" }}>
          Secciones
        </h2>
        <form onSubmit={handleCreateSection} style={{ display:"flex", gap:".6rem", marginBottom:".8rem", flexWrap:"wrap" }}>
          <input
            value={newSec} onChange={(e) => setNewSec(e.target.value)}
            placeholder="Nueva sección (ej: 2024-I, Grupo A)"
            style={{ flex:1, minWidth:180, fontFamily:"var(--font-body)", fontSize:"0.85rem", padding:"7px 10px", border:"1px solid var(--border)", borderRadius:6, outline:"none" }}
          />
          <button type="submit" disabled={savingSec} className="btn-primary" style={{ fontSize:"0.78rem", maxWidth:"none" }}>
            {savingSec ? "Creando..." : "+ Crear"}
          </button>
        </form>
        {secError && <p style={{ color:"#c0392b", fontSize:"0.78rem", marginBottom:".5rem" }}>{secError}</p>}
        <div style={{ display:"flex", flexWrap:"wrap", gap:".5rem" }}>
          {sections.map((sec) => (
            <div key={sec.id} style={{ display:"flex", alignItems:"center", gap:".4rem", background:"var(--teal-light)", borderRadius:20, padding:"4px 14px", maxWidth:"none" }}>
              <span style={{ fontFamily:"var(--font-display)", fontWeight:600, fontSize:"0.78rem", color:"var(--teal-dark)", maxWidth:"none" }}>{sec.name}</span>
              <span style={{ fontSize:"0.68rem", color:"var(--teal)", maxWidth:"none" }}>({sec.student_count || 0})</span>
              <button
                onClick={() => handleDeleteSection(sec.id, sec.name)}
                style={{ background:"none", border:"none", color:"var(--teal-dark)", cursor:"pointer", fontSize:"0.75rem", padding:"0 2px", maxWidth:"none" }}
              >✕</button>
            </div>
          ))}
          {sections.length === 0 && <p style={{ fontSize:"0.78rem", color:"var(--text-muted)" }}>No hay secciones. Crea una arriba.</p>}
        </div>
      </div>

      {/* ── Formulario crear/editar ── */}
      <div className="card">
        <h2 style={{ fontFamily:"var(--font-display)", fontWeight:800, fontSize:"0.95rem", color:"var(--teal-dark)", marginBottom:"1rem" }}>
          {editId ? "✏️ Editar alumno" : "Crear cuenta de alumno"}
        </h2>
        {error   && <p style={{ color:"#c0392b", fontSize:"0.82rem", marginBottom:".6rem" }}>{error}</p>}
        {success && <p style={{ color:"#27ae60", fontSize:"0.82rem", marginBottom:".6rem" }}>{success}</p>}
        <form onSubmit={handleSubmit} style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
            <div className="form-group" style={{ margin:0 }}>
              <label>Nombre</label>
              <input value={form.first_name} onChange={(e) => set("first_name",e.target.value)} placeholder="Juan" required />
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label>Apellido</label>
              <input value={form.last_name} onChange={(e) => set("last_name",e.target.value)} placeholder="Pérez" required />
            </div>
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label>Correo electrónico</label>
            <input type="email" value={form.email} onChange={(e) => set("email",e.target.value)} placeholder="alumno@correo.com" required />
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"10px" }}>
            <div className="form-group" style={{ margin:0 }}>
              <label>Código de alumno</label>
              <input value={form.code} onChange={(e) => set("code",e.target.value)} placeholder="22200073" maxLength={20} />
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label>Sección</label>
              <select value={form.section_id} onChange={(e) => set("section_id",e.target.value)}>
                <option value="">Sin sección</option>
                {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label>{editId ? "Nueva contraseña (opcional)" : "Contraseña temporal"}</label>
              <input type="password" value={form.password} onChange={(e) => set("password",e.target.value)} placeholder="Mínimo 6 caracteres" />
            </div>
          </div>
          <div style={{ display:"flex", gap:".6rem", justifyContent:"flex-end" }}>
            {editId && (
              <button type="button" className="btn-outline" style={{ maxWidth:"none" }} onClick={() => { setForm(EMPTY); setEditId(null); setError(""); setSuccess(""); }}>
                Cancelar
              </button>
            )}
            <button type="submit" className="btn-primary" disabled={saving} style={{ maxWidth:"none" }}>
              {saving ? "Guardando..." : editId ? "Guardar cambios" : "Crear alumno"}
            </button>
          </div>
        </form>
      </div>

      {/* ── Lista de alumnos ── */}
      <div className="card">
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1rem", flexWrap:"wrap", gap:".6rem" }}>
          <h2 style={{ fontFamily:"var(--font-display)", fontWeight:800, fontSize:"0.95rem", color:"var(--teal-dark)", margin:0 }}>
            Alumnos ({countLabel})
          </h2>
          <div style={{ display:"flex", gap:".6rem", flexWrap:"wrap" }}>
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar nombre, código, email..."
              style={{ fontFamily:"var(--font-body)", fontSize:"0.82rem", padding:"6px 10px", border:"1px solid var(--border)", borderRadius:6, outline:"none", width:220 }}
            />
            <select
              value={filterSec} onChange={(e) => setFilterSec(e.target.value)}
              style={{ fontFamily:"var(--font-body)", fontSize:"0.82rem", padding:"6px 10px", border:"1px solid var(--border)", borderRadius:6, outline:"none" }}
            >
              <option value="">Todas las secciones</option>
              {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>

        {loading && <p style={{ color:"var(--text-muted)", fontSize:"0.85rem" }}>Cargando...</p>}

        {!loading && filtered.length === 0 && (
          <p style={{ color:"var(--text-muted)", fontSize:"0.85rem" }}>
            {students.length === 0 ? "No hay alumnos creados aún." : "No se encontraron resultados."}
          </p>
        )}

        {!loading && filtered.length > 0 && (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", minWidth:750, borderCollapse:"collapse", fontSize:"0.82rem" }}>
              <thead>
                <tr style={{ borderBottom:"1.5px solid var(--border)", background:"var(--teal-light)" }}>
                  <SortTh label="Código"   field="code"       width={90}  />
                  <SortTh label="Nombre"   field="first_name" width={110} />
                  <SortTh label="Apellido" field="last_name"  width={130} />
                  <th style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.7rem", color:"var(--teal-dark)", textAlign:"left", padding:"8px 10px", textTransform:"uppercase", letterSpacing:.5, whiteSpace:"nowrap", width:200 }}>Email</th>
                  <th style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.7rem", color:"var(--teal-dark)", textAlign:"left", padding:"8px 10px", textTransform:"uppercase", letterSpacing:.5, whiteSpace:"nowrap", width:110 }}>Sección</th>
                  <th style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.7rem", color:"var(--teal-dark)", textAlign:"left", padding:"8px 10px", textTransform:"uppercase", letterSpacing:.5, whiteSpace:"nowrap", width:90  }}>Estado</th>
                  <th style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.7rem", color:"var(--teal-dark)", textAlign:"left", padding:"8px 10px", textTransform:"uppercase", letterSpacing:.5, whiteSpace:"nowrap", width:140 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} style={{ borderBottom:"0.5px solid var(--border)" }}
                    onMouseEnter={(e) => e.currentTarget.style.background="var(--off-white)"}
                    onMouseLeave={(e) => e.currentTarget.style.background=""}
                  >
                    <td style={{ padding:"8px 10px", fontFamily:"var(--font-display)", fontWeight:700, color:"var(--teal-dark)", whiteSpace:"nowrap" }}>
                      {s.code || <span style={{ color:"var(--text-light)", fontWeight:400 }}>—</span>}
                    </td>
                    <td style={{ padding:"8px 10px", fontWeight:500, whiteSpace:"nowrap" }}>{s.first_name}</td>
                    <td style={{ padding:"8px 10px", fontWeight:500, whiteSpace:"nowrap" }}>{s.last_name}</td>
                    <td style={{ padding:"8px 10px", color:"var(--text-muted)", fontSize:"0.78rem" }}>{s.email}</td>
                    <td style={{ padding:"8px 10px" }}>
                      {s.section_name
                        ? <span style={{ background:"var(--teal-light)", color:"var(--teal-dark)", fontFamily:"var(--font-display)", fontWeight:600, fontSize:"0.68rem", padding:"2px 10px", borderRadius:20, display:"inline-block", maxWidth:"none", whiteSpace:"nowrap" }}>{s.section_name}</span>
                        : <span style={{ color:"var(--text-light)", fontSize:"0.75rem" }}>Sin sección</span>
                      }
                    </td>
                    <td style={{ padding:"8px 10px" }}>
                      {s.must_change_password
                        ? <span style={{ background:"#fdf3dc", color:"#a07800", fontFamily:"var(--font-display)", fontWeight:600, fontSize:"0.68rem", padding:"2px 10px", borderRadius:20, display:"inline-block", maxWidth:"none", whiteSpace:"nowrap" }}>Pendiente</span>
                        : <span style={{ background:"#e8f8f0", color:"#27ae60", fontFamily:"var(--font-display)", fontWeight:600, fontSize:"0.68rem", padding:"2px 10px", borderRadius:20, display:"inline-block", maxWidth:"none", whiteSpace:"nowrap" }}>Activo</span>
                      }
                    </td>
                    <td style={{ padding:"8px 10px" }}>
                      <div style={{ display:"flex", gap:".4rem", flexShrink:0, maxWidth:"none" }}>
                        <button onClick={() => handleEdit(s)} className="btn-outline" style={{ fontSize:"0.7rem", padding:"3px 10px", whiteSpace:"nowrap", maxWidth:"none" }}>Editar</button>
                        <button onClick={() => handleDelete(s.id, `${s.first_name} ${s.last_name}`)} className="btn-danger" style={{ fontSize:"0.7rem", padding:"3px 10px", whiteSpace:"nowrap", maxWidth:"none" }}>Eliminar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}